import { useLazyQuery, useQuery } from "@apollo/client";
import { useContext, useEffect, useMemo, useState } from "react";
import {
  getCampaignKeyToTypesMap,
  getUpdatedCampaignConfiguration,
} from "../components/campaign/campaignUtils";
import { GlobalConfigContext } from "../context/GlobalConfigContext";
import { getCampaignListing } from "../graphql/queries/getCampaignListing";
import { getEngagementSummary } from "../graphql/queries/getEngagementSummary";
import { listSegments } from "../graphql/queries/listSegments";
import { FeatureToggle, useFeatureToggle } from "../utils/featureToggle";

function determineStatus(status) {
  if (status === "completed") {
    return "sent";
  }
  return "PROCESSING";
}

export const useBonusListPage = (
  initialFilters = { types: ["gift_processing"], title: "" },
  initialRowsPerPage = 20
) => {
  const { campaignsConfiguration, campaignGroups } = useContext(GlobalConfigContext);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(initialRowsPerPage);
  const [numPages, setNumPages] = useState(0);
  const [localFilters, setLocalFilters] = useState({
    title: "",
    campaignStatus: [],
    segmentations: [],
  });

  const isBonusFlowEnabled = useFeatureToggle(FeatureToggle.isBonusFlowEnabled);
  const [campaignsWithEngagement, setCampaignsWithEngagement] = useState([]);

  // Engagement summary query
  const [callGetEngagementSummary, { loading: engagementSummaryLoading }] =
    useLazyQuery(getEngagementSummary);

  // Get updated campaign configuration
  const updatedCampaignConfiguration = getUpdatedCampaignConfiguration(
    campaignsConfiguration,
    campaignGroups
  );

  // Campaign options for filters
  const campaignOptions = updatedCampaignConfiguration
    .filter(
      (campaign) => !(isBonusFlowEnabled && campaign.key === "gift_processing")
    )
    .map((campaign) => ({
      id: campaign.key,
      displayText: campaign.value?.label || campaign.name || campaign.key || "",
    }));

  // Map campaign keys to types
  const campaignKeyToTypesMap = getCampaignKeyToTypesMap(
    updatedCampaignConfiguration
  );

  // Adjust filter for API call
  const adjustedFilter = initialFilters.types
    ? {
        ...initialFilters,
        types: [
          ...new Set(
            initialFilters.types.flatMap(
              (type) => campaignKeyToTypesMap[type] || [type]
            )
          ),
        ],
      }
    : initialFilters;

  // Fetch campaign data
  const { loading, error, data, refetch } = useQuery(getCampaignListing, {
    variables: { filter: adjustedFilter },
  });

  // Fetch segments data
  const { data: segmentsData } = useQuery(listSegments, {
    variables: { filter: {} },
  });

  const segmentOptions = useMemo(
    () =>
      segmentsData?.listSegments?.map((segment) => ({
        id: segment.id,
        displayText: segment.name,
        population: segment.population,
      })),
    [segmentsData?.listSegments]
  );

  // Get filtered campaign list
  const allCampaigns = useMemo(
    () => (data?.listCampaigns?.length ? data.listCampaigns : []),
    [data?.listCampaigns]
  );

  const matchesTitleFilter = (campaign, filters) => {
    if (!filters.title) return true;

    const campaignTitle = campaign.title?.toLowerCase();
    const filterTitle = filters.title.toLowerCase();

    return campaignTitle?.includes(filterTitle);
  };

  const matchesStatusFilter = (campaign, filters) => {
    const statuses = filters.campaignStatus;

    if (!statuses || statuses.length === 0) {
      return true;
    }

    if (!campaign.campaignStatus) {
      return false;
    }

    if (
      statuses.includes("PROCESSING") &&
      ["published", "active"].includes(campaign.campaignStatus)
    ) {
      return true;
    }

    if (statuses.includes("SENT") && campaign.campaignStatus === "completed") {
      return true;
    }

    return false;
  };

  const matchesSegmentationFilter = (campaign, filters) => {
    const segmentations = filters.segmentations;

    if (!segmentations || segmentations.length === 0) {
      return true;
    }

    if (!campaign.segments) {
      return false;
    }

    const hasMatchingSegment = segmentations.some((segmentId) =>
      campaign.segments.includes(segmentId)
    );

    return hasMatchingSegment;
  };

  const campaignMatchesFilters = (campaign, filters) => {
    if (!matchesTitleFilter(campaign, filters)) return false;
    if (!matchesStatusFilter(campaign, filters)) return false;
    if (!matchesSegmentationFilter(campaign, filters)) return false;
    return true;
  };

  // Filter campaigns locally based on localFilters
  const filteredCampaigns = useMemo(() => {
    if (!allCampaigns) return [];

    return allCampaigns.filter((campaign) =>
      campaignMatchesFilters(campaign, localFilters)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCampaigns, localFilters]);

  // Calculate pagination
  useEffect(() => {
    setNumPages(Math.ceil(filteredCampaigns.length / rowsPerPage));
  }, [filteredCampaigns.length, rowsPerPage]);

  // Get current page items
  const currentPageItems = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredCampaigns.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredCampaigns, currentPage, rowsPerPage]);

  useEffect(() => {
    if (currentPageItems?.length) {
      const campaignMap = {};
      for (let campaign of currentPageItems) {
        const campaignSegmentOptions =
          campaign.segments?.map((segmentId) => {
            const segmentInfo = segmentOptions?.find((seg) => seg.id === segmentId);
            return {
              id: segmentInfo?.id || "",
              displayText: segmentInfo?.displayText || "",
              population: segmentInfo?.population || "",
            };
          }) || [];

        campaignMap[campaign.id] = {
          ...campaign,
          segmentOptions: campaignSegmentOptions,
        };
      }

      const campaignIds = Object.keys(campaignMap);
      // Fetch engagement summary data
      callGetEngagementSummary({ variables: { campaignIds } })
        .then((result) => {
          const data = result?.data;
          const campaignSymmary = data?.getEngagementSummary || [];

          for (let campaign of campaignSymmary) {
            campaignMap[campaign.campaignId] = {
              ...campaignMap[campaign.campaignId],
              summary: campaign.summary,
            };
          }

          const campaignsWithStatus = [];
          for (let key in campaignMap) {
            const campaign = campaignMap[key];
            const status = determineStatus(campaign?.campaignStatus);
            campaignsWithStatus.push({ ...campaign, status });
          }

          setCampaignsWithEngagement(campaignsWithStatus);
        })
        .catch((error) => {
          console.error("Error in engagement summary query:", error);
          setCampaignsWithEngagement(currentPageItems);
        });
    } else {
      // Reset campaigns when there are no current page items
      setCampaignsWithEngagement([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageItems, segmentOptions]);

  // Handle page change
  const handlePageChange = (newPageIndex) => {
    if (newPageIndex > 0 && newPageIndex <= numPages) {
      setCurrentPage(newPageIndex);
    }
  };

  // Update local filters
  const updateLocalFilters = (value, key) => {
    setLocalFilters((prevFilters) => ({ ...prevFilters, [key]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Set full local filters object
  const setFullLocalFilters = (newFilters) => {
    setLocalFilters(newFilters);
    setCurrentPage(1);
  };

  // Filter schemas for UI
  const filterKAZJsonSchema = {
    title: "Campaign List Filter",
    type: "object",
    properties: {
      campaignStatus: {
        label: "Status",
        items: {
          type: "string",
          options: [
            { id: "PROCESSING", displayText: "Processing" },
            { id: "SENT", displayText: "Sent" },
          ],
        },
      },
      segmentations: {
        label: "Segmentation",
        items: { type: "string", options: segmentOptions },
      },
    },
  };

  const filterBonusUiSchema = {
    type: "VerticalLayout",
    elements: [
      {
        type: "Control",
        scope: "#/properties/campaignStatus",
        component: "multiSelect",
        placeholder: "Status",
      },
      {
        type: "Control",
        scope: "#/properties/segmentations",
        component: "multiSelect",
        placeholder: "Segment",
        selection: "multiple",
      },
    ],
  };

  return {
    loading: loading || engagementSummaryLoading,
    error,
    allCampaigns,
    filteredCampaigns,
    campaigns: campaignsWithEngagement,
    refetch,
    segmentOptions,
    campaignOptions,
    filterKAZJsonSchema,
    filterBonusUiSchema,
    currentPage,
    handlePageChange,
    numPages,
    localFilters,
    updateLocalFilters,
    setLocalFilters: setFullLocalFilters,
  };
};
